import {PaginatorInfo} from "./generic.dto";
import {CostCenterDto} from "./cost-center.dto";
import { EmployeeBand, EmployeeBandDto } from "./employee-band.dto";
import { CompanyCurrencyConfigDto } from "./company-currency.dto";
import { PayPeriodDto } from "./pay-period.dto";
import { EmployeeDto } from "./employee.dto";
import { AllowanceGroupDto } from "./allowance-group.dto";

export const ALLOWANCE_FORMULA_TYPE = {
    FLAT: 'FLAT',
    PERCENT: 'PERCENT',
    HYBRID: 'HYBRID',
    CUSTOM: 'CUSTOM'
};

export type ALLOWANCE_FORMULA_TYPE = (typeof ALLOWANCE_FORMULA_TYPE)[keyof typeof ALLOWANCE_FORMULA_TYPE]

export const EMPLOYMENT_PERIOD_UNIT = {
    DAY: 'DAY',
    MONTH: 'MONTH',
    YEAR: 'YEAR'
};

export type EMPLOYMENT_PERIOD_UNIT = (typeof EMPLOYMENT_PERIOD_UNIT)[keyof typeof EMPLOYMENT_PERIOD_UNIT]

export const PAYMENT_FREQUENCY = {
    DAILY: 'DAILY',
    WEEKLY: 'WEEKLY',
    FORTNIGHTLY: 'FORTNIGHTLY',
    MONTHLY: 'MONTHLY',
    QUARTERLY: 'QUARTERLY',
    BIANNUALLY: 'BIANNUALLY',
    ANNUALLY: 'ANNUALLY',
    PRESET: 'PRESET'
};

export enum AllowanceApplicableValuePeriod {
    MONTH,
    YEAR,
    QUARTER
}

export type PAYMENT_FREQUENCY = (typeof PAYMENT_FREQUENCY)[keyof typeof PAYMENT_FREQUENCY]


export type Allowance = {
    id: number
    companyId: number
    code: string
    name: string
    description: string
    formulaType: ALLOWANCE_FORMULA_TYPE
    fixedComponent: number
    rateComponent: number
    prorate: boolean
    minEmploymentPeriod: number | null
    employmentPeriodUnit: EMPLOYMENT_PERIOD_UNIT | null
    paymentFrequency: PAYMENT_FREQUENCY | null
    paymentInstance: number | null
    minValue: number
    maxValue: number
    active: boolean
    applicableValuePeriod: AllowanceApplicableValuePeriod
    groupId: boolean | null
    createdAt: string
    modifiedAt: string | null
}
export class CreateAllowanceDto {
    companyId!: number;
    code!: string;
    name!: string;
    description!: string;
    formulaType!: ALLOWANCE_FORMULA_TYPE;
    fixedComponent!: number;
    rateComponent!: number;
    prorate!: boolean;
    minEmploymentPeriod?: number;
    employmentPeriodUnit?: EMPLOYMENT_PERIOD_UNIT;
    paymentFrequency?: PAYMENT_FREQUENCY;
    paymentInstance?: number;
    minValue!: number;
    maxValue!: number;
    active!: boolean  |  undefined;
    currencyId!: number;
    employeeIds?: number[]
    employeeBandIds?: number[]
    applicableValuePeriod!: AllowanceApplicableValuePeriod;
    payPeriodIds?: number[];
    nonTaxable?: boolean;
    groupId?: boolean;
}


export class UpdateAllowanceDto {
    companyId?: number;
    code?: string;
    name?: string;
    description?: string;
    formulaType?: ALLOWANCE_FORMULA_TYPE;
    fixedComponent?: number;
    rateComponent?: number;
    prorate?: boolean;
    minEmploymentPeriod?: number;
    employmentPeriodUnit?: EMPLOYMENT_PERIOD_UNIT;
    paymentFrequency?: PAYMENT_FREQUENCY;
    paymentInstance?: number;
    minValue?: number;
    maxValue?: number;
    active?: boolean;
    currencyId?: number;
    applicableValuePeriod?: AllowanceApplicableValuePeriod;
    addPayPeriodIds?: number[];
    removePayPeriodIds?: number[];
    addEmployeeIds?: number[];
    removeEmployeeIds?: number[];
    addEmployeeBandIds?: number[];
    removeEmployeeBandIds?: number[];
    nonTaxable?: boolean;
    groupId?: boolean;
}

export interface AllowanceDto extends Allowance {
    employeeAllowances?: EmployeeAllowanceDto[];
    employeeBandAllowances?: EmployeeBandAllowanceDto[];
    companyCurrency?: CompanyCurrencyConfigDto,
    payPeriods?: PayPeriodDto[];
    group?: AllowanceGroupDto;
}

export interface EmployeeAllowanceDto extends EmployeeAllowance {
    employee?: EmployeeDto;
}

export interface EmployeeBandAllowanceDto extends EmployeeBandAllowance {
    employeeBand?: EmployeeBand;
}

type EmployeeAllowance = {
    id: number;
    employeeId: number;
    savingsSchemeId: number;
    active: boolean;
    createdAt: string;
    modifiedAt: string | null;
}

type EmployeeBandAllowance = {
    id: number;
    employeeBandId: number;
    allowanceId: number;
    active: boolean;
    createdAt: string;
    modifiedAt: string | null;
}

export class QueryAllowanceDto {
    companyId?: number;
    formulaType?: ALLOWANCE_FORMULA_TYPE;
    prorate?: boolean;
    paymentFrequency?: PAYMENT_FREQUENCY;
    active?: boolean;
    groupId?: boolean;
    page?: number = 1;
    limit?: number;
    orderBy?: string
}

export interface AllowancePaginator extends PaginatorInfo<AllowanceDto> {}

export enum AllowanceOrderBy {
    CREATED_AT_ASC = 'createdAt:asc',
    CREATED_AT_DESC = 'createdAt:desc',
    MODIFIED_AT_ASC = 'modifiedAt:asc',
    MODIFIED_AT_DESC = 'modifiedAt:desc',
    CODE_ASC = 'code:asc',
    CODE_DESC = 'code:desc',
    NAME_ASC = 'name:asc',
    NAME_DESC = 'name:desc',
    MIN_VALUE_ASC = 'minValue:asc',
    MIN_VALUE_DESC = 'minValue:desc',
    MAX_VALUE_ASC = 'maxValue:asc',
    MAX_VALUE_DESC = 'maxValue:desc',
  }

export class SearchAllownaceDto {
    q?: string;
    companyId?: number;
    page: number = 1;
    limit?: number;
    orderBy: AllowanceOrderBy = AllowanceOrderBy.CREATED_AT_ASC;
  }

  export type AllowanceSummary = Pick<Allowance, 'id' | 'code' | 'name'>;
  
export class UploadAllowancesResponse {
  successful!: {
    allowanceId?: number;
    rowNumber?: number;
  }[];
  failed!: {
    rowNumber?: number;
    errors: {
      column: string;
      reason: string;
    }[]
  }[];
}

export interface QueryAllowanceNoPaginationDto extends Omit<QueryAllowanceDto, 'page' | 'limit'> {};

export class ExportAllowanceQueryDto {
  allowanceCode?: boolean;
  allowanceName?: boolean;
  description?: boolean;
  formulaType?: boolean;
  fixedComponent?: boolean;
  rateComponent?: boolean;
  prorate?: boolean;
  minEmploymentPeriod?: boolean;
  employmentPeriodUnit?: boolean;
  paymentFrequency?: boolean;
  paymentInstance?: boolean;
  minValue?: boolean;
  maxValue?: boolean;
  active?: boolean;
  currencyCode?: boolean;
  applicableValuePeriod?: boolean;
  nonTaxable?: boolean;
  groupCode?: boolean;
  orderBy: AllowanceOrderBy = AllowanceOrderBy.CREATED_AT_ASC;
}